import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  IconButton,
  Box,
  Typography,
  Slide,
  Paper,
} from '@mui/material';
import {
  GetApp as InstallIcon,
  Close as CloseIcon,
  CloudOff as OfflineIcon,
  CloudDone as OnlineIcon,
  SystemUpdate as UpdateIcon,
} from '@mui/icons-material';
import { usePWA } from '../contexts/PWAContext';
import { useTheme } from '../contexts/ThemeContext';

// Slide transition for snackbars
function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

// Install prompt banner
export function InstallPrompt() {
  const { isInstallable, installApp, isInstalled } = usePWA();
  const { mode } = useTheme();
  const [dismissed, setDismissed] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  // Show prompt after a delay if installable and not dismissed
  useEffect(() => {
    const wasDissmissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDissmissed) {
      const dismissedTime = parseInt(wasDissmissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return;
      }
    }

    if (isInstallable && !isInstalled && !dismissed) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 5000); // Show after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [isInstallable, isInstalled, dismissed]);

  const handleInstall = async () => {
    const installed = await installApp();
    if (installed) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <Snackbar
      open={showPrompt}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={SlideTransition}
      sx={{ mb: { xs: 2, sm: 0 } }}
    >
      <Paper
        elevation={8}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderRadius: 3,
          background: mode === 'dark'
            ? 'linear-gradient(135deg, #1a1b23 0%, #2d2e3a 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)',
          border: mode === 'dark'
            ? '1px solid rgba(255,255,255,0.1)'
            : '1px solid rgba(0,0,0,0.1)',
          maxWidth: 400,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <InstallIcon sx={{ color: 'white' }} />
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Install FlickHub
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add to home screen for quick access
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          onClick={handleInstall}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
          }}
        >
          Install
        </Button>

        <IconButton size="small" onClick={handleDismiss}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
}

// Update available banner
export function UpdatePrompt() {
  const { updateAvailable, updateApp, dismissUpdate } = usePWA();

  if (!updateAvailable) return null;

  return (
    <Snackbar
      open={updateAvailable}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={SlideTransition}
    >
      <Alert
        severity="info"
        icon={<UpdateIcon />}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              color="inherit"
              size="small"
              onClick={updateApp}
              sx={{ fontWeight: 600 }}
            >
              Update
            </Button>
            <IconButton size="small" color="inherit" onClick={dismissUpdate}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{
          borderRadius: 2,
          '& .MuiAlert-message': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        A new version of FlickHub is available!
      </Alert>
    </Snackbar>
  );
}

// Offline indicator
export function OfflineIndicator() {
  const { isOnline } = usePWA();
  const [showOnlineMessage, setShowOnlineMessage] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline) {
      setShowOnlineMessage(true);
      const timer = setTimeout(() => {
        setShowOnlineMessage(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  return (
    <>
      {/* Offline snackbar */}
      <Snackbar
        open={!isOnline}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          severity="warning"
          icon={<OfflineIcon />}
          sx={{ borderRadius: 2 }}
        >
          You're offline. Some features may be unavailable.
        </Alert>
      </Snackbar>

      {/* Back online snackbar */}
      <Snackbar
        open={showOnlineMessage}
        autoHideDuration={3000}
        onClose={() => setShowOnlineMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          severity="success"
          icon={<OnlineIcon />}
          sx={{ borderRadius: 2 }}
        >
          You're back online!
        </Alert>
      </Snackbar>
    </>
  );
}

// Combined PWA prompts component
export default function PWAPrompts() {
  return (
    <>
      <InstallPrompt />
      <UpdatePrompt />
      <OfflineIndicator />
    </>
  );
}
